import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { localCustomerDB } from '../lib/localDB';
import type { Profile } from '../lib/supabase';

interface CustomerWithBalance extends Profile {
  balance?: number;
}

export function useCustomerSearch() {
  const [searchResults, setSearchResults] = useState<CustomerWithBalance[]>([]);
  const [searching, setSearching] = useState(false);

  const fetchCustomerBalance = async (customerSmartKey: string): Promise<number> => {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('amount')
        .eq('customer_smart_key', customerSmartKey);

      if (error) throw error;
      return data?.reduce((sum, transaction) => sum + (transaction.amount || 0), 0) || 0;
    } catch (err) {
      console.error('Error fetching customer balance:', err);
      return 0;
    }
  };

  const searchCustomers = useCallback(async (query: string, userProfile: Profile) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);

    try {
      let vendorSmartKey: string;

      if (userProfile.account_type === 'shop_owner') {
        vendorSmartKey = userProfile.smart_key;
      } else if (userProfile.account_type === 'shop_assistant') {
        // Get vendor's smart key through assistant relationship
        const { data: assistantData } = await supabase
          .rpc('get_vendor_for_assistant');
        vendorSmartKey = assistantData;
      } else {
        throw new Error('Unauthorized access');
      }

      if (!vendorSmartKey) {
        throw new Error('Could not determine vendor');
      }

      // First, try to search in local cache
      try {
        const cachedResults = await localCustomerDB.searchCachedCustomers(vendorSmartKey, query);
        
        if (cachedResults.length > 0) {
          // Convert cached data to expected format and add balance
          const results = await Promise.all(
            cachedResults.slice(0, 5).map(async (customer) => ({
              ...customer,
              id: customer.smart_key, // Add required fields
              phone: '',
              country_code: '',
              date_of_birth: '',
              address: '',
              account_type: 'customer' as const,
              reference_smart_key: null,
              shop_name: null,
              business_type: null,
              created_at: '',
              updated_at: '',
              assistant_vendor_smart_key: null,
              assistant_status: null,
              recovery_email: null,
              balance: customer.balance || await fetchCustomerBalance(customer.smart_key)
            }))
          );
          
          setSearchResults(results);
          setSearching(false);
          
          // Background refresh if cache is getting stale
          const isStale = await localCustomerDB.isCacheStale(vendorSmartKey);
          if (isStale) {
            // Silently update cache in background
            refreshCacheInBackground(vendorSmartKey, query);
          }
          
          return;
        }
      } catch (cacheError) {
        console.warn('Cache search failed, falling back to server:', cacheError);
      }

      // Fallback to server search
      await searchFromServer(vendorSmartKey, query);

    } catch (err) {
      console.error('Error searching customers:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const searchFromServer = async (vendorSmartKey: string, query: string) => {
    try {
      // Get all customers linked to this vendor
      const { data: relationships } = await supabase
        .from('vendor_customer_relationships')
        .select('customer_smart_key')
        .eq('vendor_smart_key', vendorSmartKey);

      if (!relationships) return;

      const customerSmartKeys = relationships.map(r => r.customer_smart_key);

      // Search for customers in the linked customers list
      const { data: customers, error } = await supabase
        .from('profiles')
        .select('smart_key, username, currency, avatar_url')
        .in('smart_key', customerSmartKeys)
        .ilike('username', `%${query}%`)
        .limit(5);

      if (error) throw error;

      // Fetch balance for each customer and update cache
      const customersWithBalance = await Promise.all(
        (customers || []).map(async (customer) => {
          const balance = await fetchCustomerBalance(customer.smart_key);
          
          // Add to local cache
          try {
            await localCustomerDB.addCustomerToCache(vendorSmartKey, {
              smart_key: customer.smart_key,
              username: customer.username,
              currency: customer.currency,
              avatar_url: customer.avatar_url,
              balance,
              last_updated: Date.now()
            });
          } catch (cacheError) {
            console.warn('Failed to cache customer:', cacheError);
          }

          return {
            ...customer,
            balance
          };
        })
      );

      setSearchResults(customersWithBalance);
    } catch (error) {
      console.error('Server search failed:', error);
      throw error;
    }
  };

  const refreshCacheInBackground = async (vendorSmartKey: string, query: string) => {
    try {
      await searchFromServer(vendorSmartKey, query);
    } catch (error) {
      console.warn('Background cache refresh failed:', error);
    }
  };

  const syncQuickAccessToCache = async (userProfile: Profile, customers: CustomerWithBalance[]) => {
    try {
      let vendorSmartKey: string;

      if (userProfile.account_type === 'shop_owner') {
        vendorSmartKey = userProfile.smart_key;
      } else if (userProfile.account_type === 'shop_assistant') {
        const { data: assistantData } = await supabase
          .rpc('get_vendor_for_assistant');
        vendorSmartKey = assistantData;
      } else {
        return;
      }

      if (!vendorSmartKey) return;

      const cacheData = customers.map(customer => ({
        smart_key: customer.smart_key,
        username: customer.username,
        currency: customer.currency,
        avatar_url: customer.avatar_url,
        balance: customer.balance || 0,
        last_updated: Date.now()
      }));

      await localCustomerDB.cacheCustomers(vendorSmartKey, cacheData);
    } catch (error) {
      console.warn('Failed to sync quick access to cache:', error);
    }
  };

  return {
    searchResults,
    searching,
    searchCustomers,
    syncQuickAccessToCache,
    setSearchResults
  };
}